import httpRequest, { API_ROUTES } from '../lib/http/httpRequest';
import { AxiosResponse } from 'axios';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { Pull } from '@/state/exchanges/exchangesSliceTypes';
import {
    CALENDAR_PERIODS,
    CalendarNote,
    CandleHistory,
    CreateLegNotesPayload,
    DateTimeAnalyticsData,
    DistributionChartData,
    DurationSizeAnalyticsData,
    GetCalendarLegInfoPayload,
    GetDepositWithdrawalsPayload,
    GetLegNotesPayload,
    GetLegsByIdPayload,
    GetOrderTypeStatsForMultipleAccountsPayload,
    GetOrderTypeStatsPayload,
    Leg,
    LegExecutions,
    LegNote,
    LegNotes,
    MultipleAccountsRequestsPayload,
    OrderTypeStatsResponse,
    PnlChartData,
    RunPullServicePayload,
    SummaryAnalyticsData,
    SymbolsAnalyticsData,
    SymbolsFilterItem,
    UpdateLegNotesPayload,
    UpdateTransactionCommentPayload,
} from '@/state/legs/legsTypes';
import { DepositWithdrawals } from '@/state/balances/balancesTypes';
import { ResponseWithPagination } from '@/lib/http/types';
import { CalendarLegInfoResponse } from '@/state/calendar/calendarTypes';

interface GetLegExecutionsPayload {
    indexId: number;
}

interface GetCandlePayload {
    exchange: EXCHANGES[];
    symbol: string;
    timeframe: string;
    start: string;
    end: string;
    limit?: number;
}

export const getLegsById = ({
    params,
}: GetLegsByIdPayload): Promise<AxiosResponse<ResponseWithPagination<Leg>>> =>
    httpRequest.get(`${API_ROUTES.LEGS}`, {
        params: { ...params },
    });

export const getLegExecutionsApi = ({
    indexId,
}: GetLegExecutionsPayload): Promise<AxiosResponse<LegExecutions>> =>
    httpRequest.get(`${API_ROUTES.LEGS}/${indexId}/executions`);

export const getLegCandles = (payload: GetCandlePayload): Promise<AxiosResponse<CandleHistory>> =>
    httpRequest.post(`${API_ROUTES.CANDLES}`, payload);

export const getLegCSV = (params: any): Promise<AxiosResponse<string>> =>
    httpRequest.get(`${API_ROUTES.LEGS}/csv`, { params });

export const runPullService = ({
    exchange,
    apiKey,
}: RunPullServicePayload): Promise<AxiosResponse<void>> =>
    httpRequest.get(`${API_ROUTES.PULL_SERVICE}/${exchange}/${apiKey}?auto=true`);

export const getOrderTypeStats = ({
    exchange,
    accountId,
    params,
}: GetOrderTypeStatsPayload): Promise<AxiosResponse<OrderTypeStatsResponse>> =>
    httpRequest.get(`${API_ROUTES.ORDER_TYPE_STATS}/${exchange}/${accountId}`, {
        params: { ...params, sortBy: 'endDate' },
    });

export const getPullStatusApi = (
    exchange: EXCHANGES,
    accountId: number,
): Promise<AxiosResponse<Pull>> =>
    httpRequest.get(`${API_ROUTES.PERFORMANCE}/pull/${exchange}/${accountId}/status`);

export const getDepositWithdrawals = ({
    accountId,
    exchange,
    params,
}: GetDepositWithdrawalsPayload): Promise<AxiosResponse<DepositWithdrawals>> =>
    httpRequest.get(`${API_ROUTES.WITHDRAWAL_DEPOSIT}/${exchange}/${accountId}`, { params });
export const updateTransactionComment = ({
    comments,
    exchange,
    type,
    id,
}: UpdateTransactionCommentPayload): Promise<AxiosResponse<void>> =>
    httpRequest.post(`${API_ROUTES.WITHDRAWAL_DEPOSIT}/${exchange}/comment`, {
        comments,
        id,
        type,
    });

export const getLegsForMultipleAccounts = (
    params: MultipleAccountsRequestsPayload,
): Promise<AxiosResponse<ResponseWithPagination<Leg>>> =>
    httpRequest.get(`${API_ROUTES.LEGS}`, { params });

export const getSymbolsForMultipleAccounts = (
    params: MultipleAccountsRequestsPayload,
): Promise<AxiosResponse<SymbolsFilterItem[]>> =>
    httpRequest.get(`${API_ROUTES.LEGS}/symbols`, { params });

export const getPnlChartDataApi = (
    params: MultipleAccountsRequestsPayload,
): Promise<AxiosResponse<PnlChartData>> =>
    httpRequest.get(`${API_ROUTES.PNL_ANALYTICS}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });

export const getPnlChartAggregatedData = (
    params: MultipleAccountsRequestsPayload,
): Promise<AxiosResponse<PnlChartData>> =>
    httpRequest.get(`${API_ROUTES.PNL_ANALYTICS_AGGREGATED}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });

export const getDistributionChartData = (
    params: MultipleAccountsRequestsPayload,
): Promise<AxiosResponse<DistributionChartData>> =>
    httpRequest.get(`${API_ROUTES.PNL_DISTRIBUTION}`, { params });

export const getOrderTypeStatsForMultipleAccounts = (params: {
    params: GetOrderTypeStatsForMultipleAccountsPayload;
}): Promise<AxiosResponse<OrderTypeStatsResponse>> =>
    httpRequest.get(`${API_ROUTES.ORDER_TYPE_STATS}`, { params });

export const getLegLNotes = (params: GetLegNotesPayload): Promise<AxiosResponse<LegNotes>> =>
    httpRequest.get(`${API_ROUTES.LEGS_NOTES}`, { params });
export const getLegSummaryAnalytics = (
    params: GetLegNotesPayload,
): Promise<AxiosResponse<SummaryAnalyticsData>> =>
    httpRequest.get(`${API_ROUTES.LEGS_SUMMARY_ANALYTICS}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });
export const getLegSymbolsAnalytics = (
    params: GetLegNotesPayload,
): Promise<AxiosResponse<ResponseWithPagination<SymbolsAnalyticsData>>> =>
    httpRequest.get(`${API_ROUTES.LEGS_SYMBOLS_ANALYTICS}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });
export const getLegDurationAnalytics = (
    params: GetLegNotesPayload,
): Promise<AxiosResponse<DurationSizeAnalyticsData>> =>
    httpRequest.get(`${API_ROUTES.LEGS_DURATION_ANALYTICS}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });
export const getLegSizeAnalytics = (
    params: GetLegNotesPayload,
): Promise<AxiosResponse<DurationSizeAnalyticsData>> =>
    httpRequest.get(`${API_ROUTES.LEGS_SIZE_ANALYTICS}`, {
        params,
    });
export const getLegDateTimeAnalytics = (
    params: GetLegNotesPayload,
): Promise<AxiosResponse<DateTimeAnalyticsData>> =>
    httpRequest.get(`${API_ROUTES.LEGS_DATE_TIME_ANALYTICS}`, {
        params: { ...params, sortBy: ['closeDate'], order: ['desc'] },
    });
export const createLegLNotes = (payload: CreateLegNotesPayload): Promise<AxiosResponse<LegNote>> =>
    httpRequest.post(`${API_ROUTES.LEGS_NOTES}`, payload);
export const updateLegLNotes = ({
    id,
    ...rest
}: UpdateLegNotesPayload): Promise<AxiosResponse<LegNote>> =>
    httpRequest.patch(`${API_ROUTES.LEGS_NOTES}/${id}`, rest);
export const deleteLegLNotes = (id: number): Promise<AxiosResponse<LegNote>> =>
    httpRequest.delete(`${API_ROUTES.LEGS_NOTES}/${id}`);

export const getEmbedUrls = (embedUrls: string[]): Promise<AxiosResponse<string[]>> =>
    httpRequest.post(`${API_ROUTES.EMBEDS}`, { embedUrls });

export const addLegDetails = (payload: {
    legId: number;
    tagId: number;
}): Promise<AxiosResponse<{ tagIds: number[] }>> =>
    httpRequest.put(`${API_ROUTES.LEGS}/tags`, payload);

export const deleteLegDetails = (payload: {
    legId: number;
    tagId: number;
}): Promise<AxiosResponse<{ tagIds: number[] }>> =>
    httpRequest.delete(`${API_ROUTES.LEGS}/tags`, { data: payload });

export const runManualReset = (payload: {
    exchange: EXCHANGES;
    accountId: number;
}): Promise<AxiosResponse<void>> =>
    httpRequest.get(`${API_ROUTES.PERFORMANCE}/retry/${payload.exchange}/${payload.accountId}`);

export const getFuturesPnlApi = (apiKeyId: number): Promise<AxiosResponse<{ pnl: number }>> =>
    httpRequest.get(`${API_ROUTES.LEGS_FUTURES_ANALYTICS_PNL}/${apiKeyId}`);

export const getFuturesPnlHyperliquid = (
    apiKeyId: number,
): Promise<AxiosResponse<{ futuresPnl: number; overallPnl: number }>> =>
    httpRequest.get(`${API_ROUTES.LEGS_FUTURES_ANALYTICS_HL_PNL}/${apiKeyId}`);

export const getCalendarLegInfo = (
    params: GetCalendarLegInfoPayload,
): Promise<AxiosResponse<CalendarLegInfoResponse[]>> =>
    httpRequest.get(API_ROUTES.CALENDAR_LEG_INFO, { params });

export const getCalendarPeriodNotes = (params: {
    apiKeyIds: number[] | undefined;
    period: CALENDAR_PERIODS;
    startDate: string;
}): Promise<AxiosResponse<CalendarNote[]>> =>
    httpRequest.get(API_ROUTES.CALENDAR_NOTES, { params });

export const createCalendarPeriodNote = (data: {
    period: CALENDAR_PERIODS;
    startDate: string;
    note: string;
    apiKeyIds: number[] | undefined;
    embedLink?: string[];
    title?: string;
}): Promise<AxiosResponse<CalendarNote>> => httpRequest.post(API_ROUTES.CALENDAR_NOTES, data);

export const updateCalendarPeriodNote = (data: {
    id: number;
    note: string;
    embedLink?: string[];
    title?: string;
}): Promise<AxiosResponse<CalendarNote>> =>
    httpRequest.put(`${API_ROUTES.CALENDAR_NOTES}/${data.id}`, {
        note: data.note,
        embedLink: data.embedLink,
        title: data.title,
    });

export const deleteCalendarPeriodNote = ({
    id,
}: {
    id: number;
}): Promise<AxiosResponse<CalendarNote>> =>
    httpRequest.delete(`${API_ROUTES.CALENDAR_NOTES}/${id}`);

export const getMessageForSigning = (): Promise<AxiosResponse<any>> =>
    httpRequest.get(`${API_ROUTES.API_MANAGER}/dex/message`);
